// ===========================================
// CAMPAIGNS SERVICE - Production Ready
// ===========================================

import api from './api';

export interface Campaign {
  id: string;
  name: string;
  description?: string;
  type: 'EMAIL' | 'SOCIAL' | 'SMS' | 'MULTI_CHANNEL' | 'AUTOMATION';
  status: 'DRAFT' | 'SCHEDULED' | 'ACTIVE' | 'RUNNING' | 'PAUSED' | 'COMPLETED' | 'CANCELLED';
  scheduledFor?: string;
  startedAt?: string;
  completedAt?: string;
  budget?: number;
  spent: number;
  sentCount: number;
  openCount: number;
  clickCount: number;
  conversionCount: number;
  metrics: {
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    converted: number;
    revenue: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CampaignsListResponse {
  campaigns: Campaign[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CampaignFilters {
  page?: number;
  limit?: number;
  search?: string;
  type?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface CreateCampaignData {
  name: string;
  description?: string;
  type: string;
  audienceCriteria?: Record<string, unknown>;
  budget?: number;
  templateId?: string;
  destinationId?: string;
}

export const campaignsService = {
  async list(filters: CampaignFilters = {}) {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });

    return api.get<CampaignsListResponse>(`/api/campaigns?${params.toString()}`);
  },

  async getById(id: string) {
    return api.get<Campaign>(`/api/campaigns/${id}`);
  },

  async create(data: CreateCampaignData) {
    return api.post<Campaign>('/api/campaigns', data);
  },

  async update(id: string, data: Partial<CreateCampaignData>) {
    return api.patch<Campaign>(`/api/campaigns/${id}`, data);
  },

  async delete(id: string) {
    return api.delete(`/api/campaigns/${id}`);
  },

  async updateStatus(id: string, status: string) {
    return api.patch<Campaign>(`/api/campaigns/${id}/status`, { status });
  },

  async schedule(id: string, scheduledAt: string) {
    return api.post<Campaign>(`/api/campaigns/${id}/schedule`, { scheduledAt });
  },

  async duplicate(id: string, newName?: string) {
    return api.post<Campaign>(`/api/campaigns/${id}/duplicate`, { newName });
  },

  async getStats() {
    return api.get<{
      total: number;
      byStatus: Record<string, number>;
      byType: Record<string, number>;
      avgOpenRate: number;
      avgClickRate: number;
      totalRevenue: number;
    }>('/api/campaigns/stats');
  },

  async getPerformance(id: string) {
    return api.get<{
      campaign: Campaign;
      hourlyStats: Array<{ hour: string; sent: number; opened: number; clicked: number }>;
      topLinks: Array<{ url: string; clicks: number }>;
    }>(`/api/campaigns/${id}/performance`);
  },
};

export default campaignsService;
